import { IMessageBarProps, MessageBar, MessageBarType } from "@fluentui/react";
import { memo, useEffect, useState } from "react";

/**
 * Renders an error message bar when an error is provided. If no error is provided, renders nothing.
 */
export const BaseErrorMessageBar = memo(
  (
    props: { error?: any } & Omit<IMessageBarProps, "messageBarType" | "ref">,
  ) => {
    const { error, ...restProps } = props;
    const [message, setMessage] = useState<string>();
    const errorToUse = error;
    useEffect(() => {
      let disposed = false;
      getErrorMessage(errorToUse).then((message) => {
        !disposed && setMessage(message);
      });

      return () => {
        disposed = true;
      };
    }, [errorToUse, setMessage]);

    if (!errorToUse) {
      return null;
    }

    return (
      <MessageBar {...restProps} messageBarType={MessageBarType.error}>
        {message}
      </MessageBar>
    );
  },
);

const networkErrorSet = new Set(["Failed to fetch", "Load failed"]);

export async function getErrorMessage(error: any): Promise<string> {
  if (error) {
    console.error(error);
  }

  if (error instanceof TypeError && networkErrorSet.has(error.message)) {
    return "Network error. Please use AzVPN when connecting from a non-Microsoft IP.";
  } else if (error instanceof Error) {
    return `${error.name}: ${error.message}`;
  } else if (error instanceof Response) {
    return error
      .clone()
      .text()
      .catch(() => "")
      .then((response) => {
        return `${error.status} ${error.statusText}: ${response}`;
      });
  } else {
    const responseError = error?.responseJSON?.error;
    if (responseError && (responseError.code || responseError.message)) {
      return `${responseError.code}: ${responseError.message}`;
    }
    if (typeof error === "string") {
      return error;
    }
    return JSON.stringify(error);
  }
}
